import React from "react";
import {
  FileText,
  Mic,
  ShieldCheck,
  Smartphone,
  AlertCircle,
  Rocket,
  Video,
} from "lucide-react";

// SECTION: Step wizard definition
// Canonical step order for the pre-interview check. Every `currentStep`
// comparison in InterviewInstructions reads from here rather than a literal,
// so the order can be changed in one place.
export const STEP = {
  CONSENT: 1, // opens with the welcome greeting — there is no separate Welcome step
  DEVICES: 2, // camera & mic — two phases inside one step
  SECONDARY_CAMERA: 3,
  INSTRUCTIONS: 4,
  READY: 5,
};

// Phases inside STEP.DEVICES. Mic runs first, then the camera.
export const DEVICE_PHASE = {
  MIC: "mic",
  CAMERA: "camera",
};

// `icon` / `blurb` / `accent` are presentation-only fields; they carry no flow
// logic. `doneLabel` is what the Ready summary calls the step once it's behind you.
export const BASE_STEPS = [
  {
    id: STEP.CONSENT,
    title: "Consent",
    subtitle: "Consent Before Proceeding",
    blurb: "Review and agree to how the session is recorded and evaluated.",
    doneLabel: "Consent Confirmed",
    icon: ShieldCheck,
    accent: "violet",
    status: "active",
  },
  {
    id: STEP.DEVICES,
    title: "Microphone & Camera Check",
    subtitle: "Get Your Devices Ready",
    blurb: "Quick check that your laptop can see and hear you clearly.",
    doneLabel: "Microphone & Camera Ready",
    icon: Mic,
    accent: "indigo",
    status: "pending",
  },
];

// Appended only when video proctoring AND the secondary camera are both on.
export const SECONDARY_CAMERA_STEP = {
  id: STEP.SECONDARY_CAMERA,
  title: "Secondary camera",
  subtitle: "Pair Your Phone",
  blurb: "Pair your phone by scanning a code to cover your desk view.",
  doneLabel: "Secondary camera Connected",
  icon: Smartphone,
  accent: "blue",
  status: "pending",
};

// Appended only when video proctoring is enabled.
export const PROCTORING_INSTRUCTIONS_STEP = {
  id: STEP.INSTRUCTIONS,
  title: "Instructions",
  subtitle: "Rules & Guidelines",
  blurb: "Format, timing, and what to avoid during the interview.",
  doneLabel: "Instructions Reviewed",
  icon: AlertCircle,
  accent: "rose",
  status: "pending",
};

// Final confirmation before the interview starts. Always present.
export const READY_STEP = {
  id: STEP.READY,
  title: "Ready",
  subtitle: "Start Your Interview",
  blurb: "A last look at your setup before the interview begins.",
  doneLabel: "Ready to Begin",
  icon: Rocket,
  accent: "violet",
  status: "pending",
};

// SECTION: Consent checkboxes (redesigned consent step)
// Bump this whenever any consent label or description below changes. It is stored
// with every consent record, so an old record stays traceable to the exact wording
// the candidate was shown rather than to whatever the text says today.
export const CONSENT_POLICY_VERSION = "2026-09-18.2";

// The candidate must tick every item before continuing.
export const CONSENT_ITEMS = [
  {
    id: "data_sharing",
    label:
      "I consent to being recorded, my data being stored, and my data being shared with the recruitment team and other decision-makers for the hiring decision.",
    description:
      "Your recording, transcript, AI summary and application data are stored in the employer's GenieHire workspace and can be viewed by recruiters, hiring managers, interviewers and approvers involved in this role's decision.",
  },
  {
    id: "recording",
    label: "I consent to being recorded and evaluated by AI.",
    description:
      "Your video, audio and typed responses are recorded for the whole interview and assessed by GenieHire's AI, which produces a structured competency score and interview summary.",
  },
];

// Coding-challenge wording, same checkbox shape. Text carried over from
// CONSENT_INSTRUCTIONS_CODING so the agreement itself is unchanged.
export const CONSENT_ITEMS_CODING = [
  {
    id: "recording",
    label: "I consent to being recorded and evaluated by AI.",
    description:
      "Your video, audio, and submitted code are recorded and reviewed by an AI system, and may be reviewed by our team.",
  },
  {
    id: "confidentiality",
    label: "I agree to keep the challenge content confidential.",
    description:
      "Do not share or reproduce any part of the challenge. Any violations may result in disqualification.",
  },
];

// SECTION: Step wizard definition — coding-challenge flavor
export const BASE_STEPS_CODING = [
  {
    id: 1,
    title: "Mic Check",
    subtitle: "Get Mic Ready for Test",
    status: "active",
  },
  {
    id: 2,
    title: "Video Check",
    subtitle: "Get Camera Ready for Test",
    status: "pending",
  },
  {
    id: 3,
    title: "Consent",
    subtitle: "Consent Before Proceeding",
    status: "pending",
  },
];

// SECTION: Consent step copy
export const CONSENT_INSTRUCTIONS = [
  {
    icon: <FileText className="w-10 h-10" strokeWidth={1} />,
    text: (
      <div>
        <div>
          This interview will be recorded, analyzed by AI, and reviewed by the
          hiring team for evaluation only.
        </div>
        <div className="text-sm mt-4">
          By clicking Agree, you consent to its recording and use for
          evaluation purposes.
        </div>
      </div>
    ),
  },
  {
    icon: <FileText className="w-10 h-10" strokeWidth={1} />,
    text: (
      <div>
        <div>All interview content is confidential</div>
        <div className="text-sm mt-4">
          Do not share or reproduce any part of the interview. Any violations
          may result in disqualification.
        </div>
      </div>
    ),
  },
];

// SECTION: Proctoring instructions copy (correct display order)
export const PROCTORING_INSTRUCTIONS = [
  {
    title: "Camera Visibility & Proper Posture",
    text: "Keep your face, upper body, and both hands visible. Stay seated with proper posture, focused on the screen, in a well-lit environment.",
    imagePath: "/Instructions-CameraVisibility.webp",
  },
  {
    title: "Headphones Mandatory",
    text: "Earphones/headphones must stay connected throughout the interview. Disconnecting them may automatically pause or flag your interview.",
    imagePath: "/Instructions-Headphones.webp",
  },
  {
    title: "Full-Screen Mode is Mandatory",
    text: "The interview must remain in full-screen mode at all times. Tab switching, minimizing, or leaving the screen is strictly prohibited and may pause or terminate the session.",
    imagePath: "/Instructions-Assistance.webp",
  },
  {
    title: "No External Assistance or Malpractice",
    text: "No help from other people, phones, smartwatches, notes, or AI tools is allowed. Any form of cheating or suspicious behaviour will be recorded and sent to the recruitment team, and may lead to disqualification.",
    imagePath: "/Instructions-FullScreen.webp",
  },
];

// SECTION: Consent step copy — coding-challenge flavor (same structure/icons, different wording)
export const CONSENT_INSTRUCTIONS_CODING = [
  {
    icon: <FileText className="w-10 h-10" strokeWidth={1} />,
    text: (
      <div>
        <div>
          This coding challenge will be recorded, analyzed by AI, and reviewed
          by the hiring team for evaluation only.
        </div>
        <div className="text-sm mt-4">
          By clicking Agree, you consent to its recording and use for
          evaluation purposes.
        </div>
      </div>
    ),
  },
  {
    icon: <FileText className="w-10 h-10" strokeWidth={1} />,
    text: (
      <div>
        <div>All challenge content is confidential</div>
        <div className="text-sm mt-4">
          Do not share or reproduce any part of the challenge. Any violations
          may result in disqualification.
        </div>
      </div>
    ),
  },
];

// SECTION: Proctoring instructions copy — coding-challenge flavor
export const PROCTORING_INSTRUCTIONS_CODING = [
  {
    title: "Camera Visibility & Proper Posture",
    text: "Keep your face, upper body, and both hands visible. Stay seated with proper posture, focused on the screen, in a well-lit environment.",
    imagePath: "/Instructions-CameraVisibility.webp",
  },
  {
    title: "Headphones Mandatory",
    text: "Earphones/headphones must stay connected throughout the challenge. Disconnecting them may automatically pause or flag your session.",
    imagePath: "/Instructions-Headphones.webp",
  },
  {
    title: "Full-Screen Mode is Mandatory",
    text: "The challenge must remain in full-screen mode at all times. Tab switching, minimizing, or leaving the screen is strictly prohibited and may pause or terminate the session.",
    imagePath: "/Instructions-Assistance.webp",
  },
  {
    title: "No External Assistance or Malpractice",
    text: "No help from other people, phones, smartwatches, notes, or AI tools is allowed. Any form of cheating or suspicious behaviour will be recorded and sent to the recruitment team, and may lead to disqualification.",
    imagePath: "/Instructions-FullScreen.webp",
  },
];

// SECTION: Error message dictionary (keyed by getErrorMessage's `type` arg)
export const ERROR_MESSAGES = {
  micPermissionDenied:
    "Microphone permission denied. Please enable microphone access in your browser settings or try again.",
  cameraPermissionDenied:
    "Camera permission denied. Please enable camera access in your browser settings or try again.",
  noMicSelected: "Please select a microphone",
  cameraNotWorking:
    "Camera is not working. Please check your camera connection, permissions, or try another device.",
  deviceAccessError:
    "Error accessing devices. Please check your camera and try again.",
  noAudioDetected: {
    multi: "No audio detected. Please speak aloud during test, try different microphone from dropdown, or check mute settings.",
    single: "No audio detected. Please speak aloud during test, check microphone connection, or verify mute settings.",
  },
  weakAudioSignal: {
    multi: "Weak audio signal. Please speak louder, try different microphone from dropdown, or check volume settings.",
    single: "Weak audio signal. Please speak louder and closer, check volume settings, or use external microphone.",
  },
};

// SECTION: Proctoring positioning hints (keyed by validation requirement)
export const PROCTORING_HINTS = {
  handsVisible:
    "Place both hands in front of you, visible in the camera frame. Rest them on your desk or lap.",
  upperBodyVisible:
    "Move back from the camera so your shoulders and chest are visible. Try moving your chair back.",
  properPosture:
    "Keep your back straight and both shoulders at the same height. Avoid leaning to one side.",
  properDistance:
    "Adjust your distance: sit 2-3 feet from the camera. Ensure your head, shoulders, and upper chest are all in frame.",
  faceVisible:
    "Ensure your face is clearly visible and well-lit in the center of the camera view.",
};

// SECTION: Mic-test analysis tuning
export const MIC_TEST_CONFIG = {
  FFT_SIZE: 2048,
  SMOOTHING_TIME_CONSTANT: 0.8,
  TEST_DURATION_MS: 4000,
  MIN_LEVEL_THRESHOLD: 25,
  MIN_CONSISTENT_FRAMES: 2,
  BASELINE_WINDOW_MS: 1000,
  THRESHOLD_MARGIN: 10,
  NEAR_ZERO_LEVEL: 5,
  MUTE_ZERO_PCT: 95,
  MUTE_NEAR_ZERO_PCT: 90,
  MUTE_REPEAT_PCT: 80,
  MUTE_MAX_LEVEL: 10,
};

// SECTION: Lighting check tuning
// Average frame luma (Rec. 709, 0-255) sampled off the live video. Advisory
// only — it reports to the candidate but does not gate the camera step.
export const LIGHTING_CHECK = {
  // Near-zero luma is not a dim room — a dark room still reads ~15-40 once the
  // sensor applies gain. Essentially no signal means the lens is physically
  // covered (privacy shutter, sticker, closed lid).
  BLOCKED_MAX: 8,
  DARK_MAX: 55, // below this the frame reads as under-lit
  BRIGHT_MIN: 225, // above this it is blown out
  SAMPLE_INTERVAL_MS: 600,
  SAMPLE_WIDTH: 48, // downscaled target — averaging doesn't need resolution
  SAMPLE_HEIGHT: 36,
  SMOOTHING_WINDOW: 4, // rolling mean, stops the label flickering on noise
};

// SECTION: getUserMedia constraints
export const VIDEO_CONSTRAINTS = {
  WIDTH_IDEAL: 1280,
  HEIGHT_IDEAL: 720,
};

export const AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

// SECTION: MediaPipe pose-validation thresholds (values match pre-refactor behavior)
export const POSE_VALIDATION = {
  LANDMARK_VISIBILITY_THRESHOLD: 0.4,
  SHOULDER_LEVEL_MAX_DIFF: 0.11,
  BODY_ALIGNMENT_MAX_DIFF: 0.16,
  SHOULDER_WIDTH_MIN: 0.15,
  SHOULDER_WIDTH_MAX: 0.6,
  CENTER_MAX_DIFF: 0.22,
  VERTICAL_POSITION_MIN: 0.08,
  VERTICAL_POSITION_MAX: 0.45,
  POLL_INTERVAL_MS: 200,
  TARGET_VALID_FRAMES: 7,
  VALIDATION_DECAY_PER_BAD_FRAME: 3,
  FACE_X_MIN: 0.15,
  FACE_X_MAX: 0.85,
  FACE_Y_MIN: 0.05,
  FACE_Y_MAX: 0.65,
  UPPER_CHEST_MAX_Y: 0.95,
  HEAD_SHOULDER_GAP_MIN: 0.05,
  HEAD_SHOULDER_GAP_MAX: 0.5,
};

// SECTION: Timing/delay constants used across the flow
export const TIMING = {
  COUNTDOWN_SECONDS: 5,
  AUTO_ADVANCE_DELAY_MS: 3000,
  DEVICE_RETRY_DELAY_MS: 150,
  INTERVIEW_HANDOFF_DELAY_MS: 1000,
  VERIFIED_ADVANCE_DELAY_MS: 2000,
  HELP_RETEST_DELAY_MS: 100,
  SECONDARY_CAPTURE_INTERVAL_MS: 120000,
};

// SECTION: Help chat topics
// Content behind the "Need help?" button at the foot of the step rail. Each
// topic is one chip in the chat; picking it replies with `steps` as a numbered
// list. `id` doubles as the message key, so keep them unique.
export const HELP_SUPPORT = {
  email: "support@geniehire.ai",
  phone: "+91 87666 35744",
  phoneHref: "tel:+918766635744",
};

export const HELP_GREETING =
  "Hi there 👋 What are you facing problems with?";

export const HELP_TOPICS = [
  {
    id: "microphone",
    label: "Microphone",
    icon: Mic,
    lead: "Let's get your mic working. Try these in order — most issues clear up after the first two.",
    steps: [
      "Allow microphone access: click the lock icon in your browser's address bar → Site settings → set Microphone to Allow, then reload this page.",
      "Pick the right device: if you have more than one mic, choose it from the dropdown on the microphone step. A headset mic usually beats a built-in one.",
      "Check for a mute: look for a mute switch on your headset, a mute key on your laptop, and your system's input volume.",
      "Free the mic: close Zoom, Meet, Teams or any other app that may be holding onto it, then reload.",
      "Test it outside the browser: record a few seconds in your device's Voice Recorder app. If nothing records there, it's a system issue — restart your device or try another browser.",
    ],
  },
  {
    id: "camera",
    label: "Camera",
    icon: Video,
    lead: "Camera trouble is almost always permissions or another app holding the lens.",
    steps: [
      "Allow camera access: click the lock icon in the address bar → Site settings → set Camera to Allow, then reload this page.",
      "Free the camera: only one app can use the webcam at a time — close Zoom, Meet, Teams, OBS or your camera app.",
      "Pick the right device: if you have an external webcam, select it from the dropdown on the camera step.",
      "Check the shutter: many laptops and webcams have a physical privacy cover or a camera-off key. If you're on a USB webcam, unplug and reconnect it.",
      "Fix your lighting: face a window or a lamp, and avoid a bright light directly behind you.",
      "Frame yourself: sit 2-3 feet from the camera so your head, shoulders and upper chest are in view, with both hands visible.",
    ],
  },
  {
    id: "secondaryCamera",
    label: "Secondary camera",
    icon: Smartphone,
    lead: "Your phone becomes the second camera. Here's how to get it connected and keep it there.",
    steps: [
      "Scan the QR code: open your phone's default Camera app, point it at the code on screen, and tap the link that appears.",
      "Allow camera on the phone: tap Allow when the phone's browser asks for camera access.",
      "Nothing happening? Tap Generate new QR and scan again — codes expire. Make sure the phone is on Wi-Fi or mobile data.",
      "Position the phone: place it 2-3 feet to your side at desk height, so your hands, your screen and your desk are all in frame.",
      "Keep it awake: plug in the charger, turn on Do Not Disturb, and leave the interview tab open — don't lock the screen or switch apps.",
    ],
  },
];
